'use client';

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import useGlobalSignup from './useGlobalSignup';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useInterval } from 'react-use';
import ImageComparisonSlider from '@/components/ImageComparisonSlider';
import { motion, AnimatePresence } from 'framer-motion';
import { useSignupModal } from '@/zustand/store';
import Link from 'next/link';
let curEmail: string;

interface GlobalSignLoginModalProps {
  asChild?: boolean;
  children?: React.ReactNode;
  // 新增 prop，控制是否在点击对话框外部时阻止默认行为，默认为 true
  preventInteractOutside?: boolean;
  customClassName?: string;
}

export const EmailSchema = z.object({
  email: z
    .string()
    .trim()
    .email({ message: 'Please enter a valid email address' })
    .transform((val) => val.trim()),
});

export const CodeSchema = z.object({
  code: z
    .string()
    .trim()
    .length(6, 'code must be 6 characters')
    .transform((val) => val.trim()),
});

const GlobalSignupModal = ({
  asChild,
  children,
  preventInteractOutside = true,
  customClassName,
}: GlobalSignLoginModalProps) => {
  const isSignupModalOpen = useSignupModal((s) => s.isSignupModalOpen);
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);
  const signupModalContext = useSignupModal((s) => s.signupModalContext);
  const {
    loginViaGoogle,
    sendCode,
    sendCodePending,
    getCustomToken,
    isCustomTokenPending,
    loginViaCustomToken,
    isLoginWithCustomTokenPending,
  } = useGlobalSignup({
    setModalVisible: setSignupModalOpen,
    signupSuccess: signupModalContext?.signupSuccess,
  });

  const [errorTip, setErrorTip] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [step, setStep] = useState<'email' | 'verification'>('email');

  const form = useForm<any>({
    resolver: zodResolver(step === 'email' ? EmailSchema : CodeSchema),
    defaultValues: step === 'email' ? { email: '' } : { code: '' },
  });

  // 处理重新发送验证码
  const handleResendCode = async (isStep = false) => {
    try {
      setErrorTip('');
      await sendCode(curEmail);
      setCountdown(30); // 重置倒计时30秒
      isStep && setStep('verification');
      toast.success('Verification code has been resent');
    } catch (error: any) {
      const message = (error as Error).message;
      setErrorTip(message);
      toast.error(message);
    }
  };

  const handleSubmit = async (
    values: z.infer<typeof EmailSchema | typeof CodeSchema>
  ) => {
    setErrorTip('');
    if (step === 'email') {
      const email = (values as z.infer<typeof EmailSchema>).email;
      curEmail = email;
      await handleResendCode(true);
    } else {
      try {
        const customToken = await getCustomToken(
          curEmail,
          (values as z.infer<typeof CodeSchema>).code
        );
        await loginViaCustomToken(customToken);
      } catch (error: any) {
        const message = (error as Error).message;
        setErrorTip(message);
        toast.error(message);
      }
    }
  };

  // 倒计时逻辑
  useInterval(
    () => {
      if (countdown > 0) {
        setCountdown((c) => c - 1);
      }
    },
    countdown > 0 ? 1000 : null
  );

  useEffect(() => {
    form.reset({ ...form.getValues(), code: '' });
    form.clearErrors();
    setErrorTip('');
  }, [step, form]);

  useEffect(() => {
    if (!isSignupModalOpen) {
      form.reset({ email: '', code: '' });
      form.clearErrors();
      setStep('email');
      setCountdown(0);
      setErrorTip('');
    }
  }, [form, isSignupModalOpen]);

  return (
    <Dialog open={isSignupModalOpen} onOpenChange={setSignupModalOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent
        className={`flex w-[21.25rem] max-w-full flex-row gap-x-0 gap-y-4 overflow-hidden rounded-lg border-none p-0 lg:w-[48rem] ${customClassName}`}
        onInteractOutside={(e) => {
          if (preventInteractOutside) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <AnimatePresence initial={false} mode='wait'>
          <motion.div
            key={step}
            initial={{
              x: step === 'email' ? -30 : 30,
              opacity: 0,
            }}
            animate={{
              x: 0,
              opacity: 1,
              transition: {
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1],
              },
            }}
            exit={{
              x: step === 'email' ? -30 : 30,
              opacity: 0,
              transition: {
                duration: 0.25,
                ease: [0.4, 0, 1, 1],
              },
            }}
            transition={{ type: 'tween', duration: 0.2 }}
            className='flex w-[21.25rem] flex-col gap-4 overflow-hidden p-6'
          >
            <DialogHeader>
              <DialogTitle className='flex flex-row items-center'>
                {step === 'verification' && (
                  <Button
                    variant={'ghost'}
                    className='mr-2 h-8 w-8 rounded-full p-0'
                  >
                    <ChevronLeft
                      className='h-6 w-6 cursor-pointer'
                      onClick={() => setStep('email')}
                    />
                  </Button>
                )}
                <span className='text-2xl font-semibold'>
                  {step === 'email' ? 'Continue to Kaze' : 'Finish logging in'}
                </span>
              </DialogTitle>
            </DialogHeader>
            {step === 'email' ? (
              <DialogDescription className='text-sm leading-5 text-gray-600'>
                {signupModalContext?.description ||
                  'Sign up or log in to continue.'}
              </DialogDescription>
            ) : null}
            {step === 'email' ? (
              <div className='flex flex-col gap-3'>
                {errorTip && step === 'email' ? (
                  <div className='rounded-lg bg-[#f8d8d9] px-3 py-1 text-xs'>
                    {errorTip}
                  </div>
                ) : null}

                <Button
                  onClick={loginViaGoogle}
                  variant='outline'
                  className='flex h-11 w-full items-center justify-center gap-3 rounded-xl border-2 text-sm font-bold hover:shadow'
                >
                  <Image
                    src='https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg'
                    width={24}
                    height={24}
                    alt='Google'
                    className='h-6 w-6 pr-2'
                  />
                  Continue with Google
                </Button>

                {/* Divider */}
                <div className='relative'>
                  <div className='absolute inset-0 flex items-center'>
                    <div className='w-full border-t border-gray-200'></div>
                  </div>
                  <div className='relative flex justify-center'>
                    <span className='bg-[#f8f8f8] px-4 text-base text-gray-500'>
                      OR
                    </span>
                  </div>
                </div>

                {/* Email Form */}
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className='flex flex-col gap-y-4'
                  >
                    <div className='space-y-2'>
                      <FormField
                        control={form.control}
                        name='email'
                        render={({ field }) => (
                          <FormItem className='flex flex-col'>
                            <FormLabel htmlFor='email' />
                            <FormControl>
                              <Input
                                type='email'
                                placeholder='Enter your email'
                                id='email'
                                className='h-11 rounded-xl border border-zinc-400 px-4 text-sm ring-offset-0 hover:border-gray-600 focus:border-gray-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-transparent'
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className='text-xs text-red-400' />
                          </FormItem>
                        )}
                      />
                      <Button
                        type='submit'
                        role='button'
                        className={`h-11 w-full rounded-xl bg-gray-800 text-sm font-bold hover:bg-gray-800/90 ${sendCodePending ? 'pointer-events-none' : 'pointer-events-auto'}`}
                      >
                        {sendCodePending ? (
                          <Loader2 className='animate-spin' size={24} />
                        ) : (
                          'Continue with Email'
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>

                {/* Terms */}
                <p className='mt-6 text-xs text-neutral-500'>
                  By continuing up, you agree to our{' '}
                  <Link href='/terms' className='underline'>
                    Terms of Service
                  </Link>{' '}
                  and our{' '}
                  <Link href='/privacy' className='underline'>
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            ) : (
              <div className='flex flex-col gap-4'>
                <p className='text-sm text-neutral-900'>
                  Once you enter the code we sent to {form.getValues('email')},
                  you&apos;ll be all logged in.
                </p>

                {errorTip && step === 'verification' ? (
                  <div className='rounded-lg bg-[#f8d8d9] px-3 py-2 text-xs'>
                    {errorTip}
                  </div>
                ) : null}

                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className='flex flex-col gap-y-4'
                  >
                    <div className='space-y-3'>
                      <FormField
                        control={form.control}
                        name='code'
                        render={({ field }) => (
                          <FormItem className='flex flex-col'>
                            <FormLabel htmlFor='code'>Code</FormLabel>
                            <FormControl>
                              <Input
                                placeholder='Enter code'
                                id='code'
                                className='h-10 rounded-xl border border-zinc-400 px-4 text-sm ring-offset-0 hover:border-gray-600 focus:border-gray-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-transparent'
                                {...field}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ''
                                  );
                                  field.onChange(value);
                                  if (value.length === 6) {
                                    form.handleSubmit(handleSubmit)();
                                  }
                                }}
                                maxLength={6}
                              />
                            </FormControl>
                            <FormMessage className='text-xs text-red-400' />
                          </FormItem>
                        )}
                      />
                      <Button
                        type='submit'
                        role='button'
                        className={`h-11 w-full rounded-xl bg-gray-800 text-sm font-bold hover:bg-gray-800/90 ${
                          isCustomTokenPending ||
                          isLoginWithCustomTokenPending ||
                          sendCodePending
                            ? 'pointer-events-none'
                            : 'pointer-events-auto'
                        }`}
                      >
                        {isCustomTokenPending ||
                        isLoginWithCustomTokenPending ||
                        sendCodePending ? (
                          <Loader2 className='animate-spin' size={24} />
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
                <div className='mt-2'>
                  <p className='text-sm text-gray-600'>
                    Didn&apos;t get the code?{' '}
                    {countdown > 0 ? (
                      <span>Resend in {countdown} seconds.</span>
                    ) : (
                      <span
                        className={`cursor-pointer text-blue-600 underline ${sendCodePending ? 'pointer-events-none' : 'pointer-events-auto'}`}
                        onClick={() => handleResendCode()}
                      >
                        Resend code
                      </span>
                    )}
                  </p>
                  <button className='mt-4 inline-block text-sm text-blue-600'>
                    <Link href='/contact-us' rel='noopener noreferrer'>
                      Need help?
                    </Link>
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        <div className='hidden flex-1 lg:block'>
          <ImageComparisonSlider
            customClassName='rounded-l-none rounded-right-lg'
            afterImage='https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/8f605f98-5587-4752-5eaa-f5afed0bca00/public'
            beforeImage='https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/7c7de676-9c99-4bab-3a8c-ac5ac4170000/public'
            beforePlaceholder='blur'
            beforeBlurDataURL='data:image/jpeg;base64,/9j/4QDKRXhpZgAATU0AKgAAAAgABgESAAMAAAABAAEAAAEaAAUAAAABAAAAVgEbAAUAAAABAAAAXgEoAAMAAAABAAIAAAITAAMAAAABAAEAAIdpAAQAAAABAAAAZgAAAAAAAAEsAAAAAQAAASwAAAABAAeQAAAHAAAABDAyMjGRAQAHAAAABAECAwCgAAAHAAAABDAxMDCgAQADAAAAAQABAACgAgAEAAAAAQAAAAegAwAEAAAAAQAAAAWkBgADAAAAAQAAAAAAAAAAAAD/2wCEAAEBAQEBAQIBAQIDAgICAwQDAwMDBAUEBAQEBAUGBQUFBQUFBgYGBgYGBgYHBwcHBwcICAgICAkJCQkJCQkJCQkBAQEBAgICBAICBAkGBQYJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCf/dAAQAAf/AABEIAAUABwMBIgACEQEDEQH/xAGiAAABBQEBAQEBAQAAAAAAAAAAAQIDBAUGBwgJCgsQAAIBAwMCBAMFBQQEAAABfQECAwAEEQUSITFBBhNRYQcicRQygZGhCCNCscEVUtHwJDNicoIJChYXGBkaJSYnKCkqNDU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6g4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2drh4uPk5ebn6Onq8fLz9PX29/j5+gEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoLEQACAQIEBAMEBwUEBAABAncAAQIDEQQFITEGEkFRB2FxEyIygQgUQpGhscEJIzNS8BVictEKFiQ04SXxFxgZGiYnKCkqNTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqCg4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2dri4+Tl5ufo6ery8/T19vf4+fr/2gAMAwEAAhEDEQA/APw80341eKNO/YP8M/EO1knTWbnxfqejeal1OkaWVqkjRRhEZfu7cDLELk7QoJFfPn/DUXxa/wCgjcf+BV1/8erfT/lG74P/AOyh63/6Jlr5Wr5HJ6kmql39uX5n3+c4eEPYqKS9yP5H/9k='
            afterPlaceholder='blur'
            afterBlurDataURL='data:image/jpeg;base64,/9j/4QDKRXhpZgAATU0AKgAAAAgABgESAAMAAAABAAEAAAEaAAUAAAABAAAAVgEbAAUAAAABAAAAXgEoAAMAAAABAAIAAAITAAMAAAABAAEAAIdpAAQAAAABAAAAZgAAAAAAAAEsAAAAAQAAASwAAAABAAeQAAAHAAAABDAyMjGRAQAHAAAABAECAwCgAAAHAAAABDAxMDCgAQADAAAAAQABAACgAgAEAAAAAQAAAAegAwAEAAAAAQAAAAWkBgADAAAAAQAAAAAAAAAAAAD/2wCEAAEBAQEBAQIBAQIDAgICAwQDAwMDBAUEBAQEBAUGBQUFBQUFBgYGBgYGBgYHBwcHBwcICAgICAkJCQkJCQkJCQkBAQEBAgICBAICBAkGBQYJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCQkJCf/dAAQAAf/AABEIAAUABwMBIgACEQEDEQH/xAGiAAABBQEBAQEBAQAAAAAAAAAAAQIDBAUGBwgJCgsQAAIBAwMCBAMFBQQEAAABfQECAwAEEQUSITFBBhNRYQcicRQygZGhCCNCscEVUtHwJDNicoIJChYXGBkaJSYnKCkqNDU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6g4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2drh4uPk5ebn6Onq8fLz9PX29/j5+gEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoLEQACAQIEBAMEBwUEBAABAncAAQIDEQQFITEGEkFRB2FxEyIygQgUQpGhscEJIzNS8BVictEKFiQ04SXxFxgZGiYnKCkqNTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqCg4SFhoeIiYqSk5SVlpeYmZqio6Slpqeoqaqys7S1tre4ubrCw8TFxsfIycrS09TV1tfY2dri4+Tl5ufo6ery8/T19vf4+fr/2gAMAwEAAhEDEQA/APiGx8ea5bfsp6H4nsp549Ul8R3mniYTyhFtoYRIibFZc7c4GTx2Aya8k/4Wz8Tv+gvL/wB9z/8Ax6uktP8Akzvw7/2N+o/+kq145Xg5U21Uv/NL8z63NqMI+ysvsR/I/9k='
            width={356}
            height={400}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSignupModal;
